import React, { useMemo, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useGetPhotos } from "data/useGetPhotos";
import { usePhotoActions } from "data/usePhotoActions";
import { motion } from "framer-motion";
import { Container, ContentWithVerticalPadding } from "components/misc/Layouts";
import { SectionHeading } from "components/misc/Headings";
import { getAbsoluteUrl } from "utils/urlUtils";
import { ReactComponent as HeartIcon } from "feather-icons/dist/icons/heart.svg";
import { ReactComponent as ThumbsDownIcon } from "feather-icons/dist/icons/thumbs-down.svg";

const Section = tw(ContentWithVerticalPadding)`bg-gray-100`;
const HeaderWrapper = tw.div`relative mb-8`;
const HeaderContent = tw.div`text-center`;
const SectionHeadingStyled = tw(SectionHeading)`text-primary-900`;
const SectionDescription = tw.p`text-gray-600 mt-4 max-w-2xl mx-auto`;

const PhotosGrid = styled.div`
  ${tw`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mt-12`}
`;

const PhotoImage = styled.img`
  ${tw`w-full h-full object-cover transition-transform duration-300`}
`;

const PhotoOverlay = styled.div`
  ${tw`absolute inset-0 bg-black bg-opacity-0 transition-all duration-300 flex flex-col items-center justify-between p-4`}
`;

const LikeCountBadge = styled.div`
  ${tw`absolute bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold transition-opacity duration-300 flex items-center gap-1`}
  top: 8px;
  right: 8px;
`;

const ActionButtonsContainer = styled.div`
  ${tw`flex gap-3 opacity-0 transition-opacity duration-300`}
`;

const PhotoCard = styled(motion.div)`
  ${tw`relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 bg-gray-200 cursor-pointer`}
  aspect-ratio: 1 / 1;

  &:hover img {
    transform: scale(1.08);
  }

  &:hover ${PhotoOverlay} {
    background-color: rgba(0, 0, 0, 0.5);
  }

  &:hover ${ActionButtonsContainer} {
    opacity: 1;
  }
`;

const LikeButton = styled.button`
  ${tw`flex items-center gap-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-all duration-200`}

  &:disabled {
    ${tw`opacity-50 cursor-not-allowed`}
  }

  svg {
    ${tw`w-5 h-5`}
  }
`;

const DislikeButton = styled.button`
  ${tw`flex items-center gap-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-all duration-200`}

  &:disabled {
    ${tw`opacity-50 cursor-not-allowed`}
  }

  svg {
    ${tw`w-5 h-5`}
  }
`;

const ViewAllButton = styled(Link)`
  ${tw`inline-flex [flex-direction: row-reverse] mt-4 items-center px-6 py-2 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap`}
`;

const LoadingContainer = tw.div`text-center py-12`;
const LoadingText = tw.p`text-gray-600`;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

/**
 * RecentPhotosSection Component
 * Displays recent 6-9 photos on the home page
 * Links to the full photo gallery page
 */
export default function RecentPhotosSection() {
  const { data: photos, isLoading } = useGetPhotos();
  const { likePhoto, dislikePhoto } = usePhotoActions();
  const [updatedLikes, setUpdatedLikes] = useState({});

  // Get recent 9 photos (or fewer if not available)
  const recentPhotos = useMemo(() => {
    if (!photos || photos.length === 0) return [];
    // Sort by date descending and take first 9
    return photos
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      })
      .slice(0, 12);
  }, [photos]);

  const handleLike = async (e, photo) => {
    e.stopPropagation();
    try {
      // Optimistically update before API call
      const currentLikes = getLikeCount(photo);
      setUpdatedLikes((prev) => ({
        ...prev,
        [photo.id]: currentLikes + 1,
      }));

      await likePhoto.mutateAsync(photo.id);
    } catch (error) {
      console.error("Failed to like photo");
      // Revert on error
      setUpdatedLikes((prev) => {
        const copy = { ...prev };
        delete copy[photo.id];
        return copy;
      });
    }
  };

  const handleDislike = async (e, photoId) => {
    e.stopPropagation();
    try {
      await dislikePhoto.mutateAsync(photoId);
    } catch (error) {
      console.error("Failed to dislike photo");
    }
  };

  const getLikeCount = (photo) => {
    // Check if we have an updated count first
    if (updatedLikes[photo?.id] !== undefined) {
      return updatedLikes[photo.id];
    }
    // Fall back to the photo's likeCount, likes, or 0
    return photo?.likeCount || photo?.likes || 0;
  };
  if (isLoading) {
    return (
      <Section>
        <Container>
          <LoadingContainer>
            <LoadingText>Loading photos...</LoadingText>
          </LoadingContainer>
        </Container>
      </Section>
    );
  }

  if (!recentPhotos || recentPhotos.length === 0) {
    return null; // Don't show section if no photos
  }

  return (
    <Section>
      <Container>
        <HeaderWrapper>
          <HeaderContent>
            <SectionHeadingStyled>Photo Gallery</SectionHeadingStyled>
            <SectionDescription>
              Browse through our latest moments and memories from events and
              campus life.
            </SectionDescription>
          </HeaderContent>
          <div tw="absolute top-0 right-0">
            <ViewAllButton to="/photo-gallery">View All Photos</ViewAllButton>
          </div>
        </HeaderWrapper>

        <PhotosGrid
          as={motion.div}
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          {recentPhotos.map((photo, index) => (
            <PhotoCard
              key={`${photo.id}-${index}`}
              variants={item}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3 }}
            >
              {photo.imageUrl && (
                <>
                  <PhotoImage
                    src={getAbsoluteUrl(photo.imageUrl)}
                    alt={photo.title || `Photo ${index + 1}`}
                  />
                  <LikeCountBadge>
                    <HeartIcon tw="w-4 h-4" />
                    {getLikeCount(photo)}
                  </LikeCountBadge>
                  <PhotoOverlay>
                    <ActionButtonsContainer>
                      <LikeButton
                        onClick={(e) => handleLike(e, photo)}
                        disabled={likePhoto.isPending}
                      >
                        <HeartIcon />
                        Like
                      </LikeButton>
                    </ActionButtonsContainer>
                  </PhotoOverlay>
                </>
              )}
            </PhotoCard>
          ))}
        </PhotosGrid>
      </Container>
    </Section>
  );
}
